/**
 * @file DocumentMetaGrid.tsx
 * @description Плитки «кто / когда / итоги» в шапке документа
 * (phase19.23 этап A).
 *
 * Домен-нейтральный: приёмка, списание, перемещение, а дальше заказ и счёт
 * показывают одинаковый блок — читатель документа всегда находит «кто создал»
 * и «когда провели» на одном и том же месте.
 *
 * 2 колонки на 360px, 4 на `lg` — плитка остаётся читаемой и не режет подписи.
 *
 * Высота плитки задана снизу (`min-h-16`): плитка «Собрано» у сборки носит
 * тонкую полосу прогресса и была на 8px выше соседних, из-за чего ряд плиток
 * выглядел кривым (замер ui-visual-qa 2026-09-20).
 *
 * phase19.31 (SC43): значения — обычным шрифтом; `mono` даёт только
 * табличные цифры (даты и «3 · 12 шт» моноширинным читались как код).
 * Тон значения — цветами статусов темы, а не зашитыми.
 *
 * phase19.31 (SC69): блок целиком на непрозрачной поверхности — плитки белые,
 * линии и рамка непрозрачным токеном линии; при 1–3 плитках на `lg` столько
 * же колонок (пустых клеток нет).
 *
 * ```tsx
 * <DocumentMetaGrid
 *   items={[
 *     { label: "Создал", value: "Кривошеев Е." },
 *     { label: "Создано", value: "01.09 10:12" },
 *     { label: "Проведено", value: "01.09 10:20", tone: "success" },
 *     { label: "Итого", value: "3 · 12 шт", mono: true },
 *   ]}
 * />
 * ```
 */

import type { ReactNode } from "react";

import { SectionLabel } from "./SectionLabel";
import { cn } from "@/lib/utils";

/** Одна плитка: подпись сверху, значение снизу. */
export interface DocumentMetaItem {
  label: string;
  value: ReactNode;
  /** Акцент значения: успех (проведено) или предупреждение (черновик, ошибка). */
  tone?: "default" | "success" | "warning";
  /** Числа — табличными цифрами (моноширинный шрифт — только для кодов). */
  mono?: boolean;
}

interface Props {
  items: DocumentMetaItem[];
  className?: string;
}

const TONE: Record<NonNullable<DocumentMetaItem["tone"]>, string> = {
  default: "text-on-surface",
  success: "text-[var(--status-success-fg,var(--success))]",
  warning: "text-[var(--status-attention-fg,var(--warning))]",
};

/** Колонок на `lg` по числу плиток: 3 плитки — три в ряд, без пустой клетки. */
const LG_COLS: Record<number, string> = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
};

export function DocumentMetaGrid({ items, className }: Props) {
  if (items.length === 0) return null;
  const single = items.length === 1;
  return (
    <dl
      className={cn(
        // SC69: линии и рамка — непрозрачный токен линии темы (`gap-px` —
        // разделители, внешняя тень 1px — рамка по скруглению); раньше
        // полупрозрачная подложка пропускала фон кабинета в зазоры.
        "grid gap-px overflow-hidden rounded-xl bg-[var(--border)] [box-shadow:0_0_0_1px_var(--border)]",
        single ? "grid-cols-1" : "grid-cols-2",
        LG_COLS[items.length] ?? "lg:grid-cols-4",
        // Нечётное число на телефоне: последняя плитка на всю ширину, а не
        // пустая серая клетка рядом.
        !single && "[&>:last-child:nth-child(odd)]:col-span-2 lg:[&>:last-child:nth-child(odd)]:col-span-1",
        className,
      )}
    >
      {items.map((item) => (
        <div
          key={item.label}
          className="flex min-h-16 flex-col gap-0.5 bg-surface-lowest px-3 py-2.5"
        >
          <SectionLabel as="dt">{item.label}</SectionLabel>
          <dd
            className={cn(
              "type-body font-medium",
              item.mono && "tabular-nums",
              TONE[item.tone ?? "default"],
            )}
          >
            {item.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}
